import { logger } from '@/engine/logging';
import { itemsDB } from './ItemsDB';
import { getAllLootPools } from '@/generated/lootPools';
import { getAllLootPoolEntries } from '@/generated/lootPoolEntries';

export interface LootEntry {
  itemId: string;
  weight: number;
  minQuantity: number;
  maxQuantity: number;
  notes?: string | null;
}

export interface LootTable {
  tableId: string;
  name: string;
  entries: LootEntry[];
  totalWeight: number;
  rolls: number;
  pityKind?: string | null;
  pityN?: number | null;
}

export class LootDB {
  private static instance: LootDB | null = null;
  private lootTables = new Map<string, LootTable>();

  public static getInstance(): LootDB {
    if (!LootDB.instance) {
      LootDB.instance = new LootDB();
    }
    return LootDB.instance;
  }

  constructor() {
    this.initializeLootTables();
  }

  /**
   * Инициализация таблиц дропа из сгенерированных данных
   */
  private initializeLootTables(): void {
    // Получаем все пулы дропа
    const lootPools = getAllLootPools();
    const lootPoolEntries = getAllLootPoolEntries();

    // Создаем таблицы для каждого пула
    for (const pool of lootPools) {
      const entries = lootPoolEntries
        .filter(entry => entry.loot_pool_id === pool.loot_pool_id)
        .map(entry => ({
          itemId: entry.item_id,
          weight: entry.weight,
          minQuantity: entry.qty_min,
          maxQuantity: entry.qty_max,
          notes: entry.notes,
        }));

      if (entries.length > 0) {
        this.createLootTableFromPool(pool, entries);
      }
    }

    // Создаем маппинг размеров астероидов на пулы дропа
    this.createAsteroidSizeMapping();

    logger.info('🎲 LootDB: таблицы дропа инициализированы из сгенерированных данных', {
      tablesCount: this.lootTables.size,
      source: 'loot',
    });
  }

  /**
   * Создать таблицу дропа из пула
   */
  private createLootTableFromPool(pool: any, entries: LootEntry[]): void {
    // Маппим itemId к существующим предметам
    const mappedEntries = entries.map(entry => ({
      ...entry,
      itemId: this.mapItemId(entry.itemId),
    }));

    const totalWeight = mappedEntries.reduce((sum, entry) => sum + entry.weight, 0);

    const lootTable: LootTable = {
      tableId: pool.loot_pool_id,
      name: pool.notes || pool.loot_pool_id,
      entries: mappedEntries,
      totalWeight,
      rolls: pool.rolls,
      pityKind: pool.pity_kind,
      pityN: pool.pity_N,
    };

    this.lootTables.set(pool.loot_pool_id, lootTable);
  }

  /**
   * Создать маппинг размеров астероидов на пулы дропа
   */
  private createAsteroidSizeMapping(): void {
    // Маппинг размеров астероидов на пулы дропа
    const asteroidSizeMapping = {
      pebble: 'LP_COMMON',
      small: 'LP_COMMON',
      medium: 'LP_QUARTZ',
      large: 'LP_FERRO',
      colossal: 'LP_LUMEN',
    };

    // Создаем таблицы для размеров астероидов
    for (const [size, poolId] of Object.entries(asteroidSizeMapping)) {
      const pool = this.lootTables.get(poolId);
      if (pool) {
        // Создаем копию таблицы для размера астероида
        const asteroidTable: LootTable = {
          ...pool,
          tableId: size,
          name: `${size.charAt(0).toUpperCase() + size.slice(1)} Asteroid`,
        };
        this.lootTables.set(size, asteroidTable);
      }
    }
  }

  /**
   * Маппинг itemId из lootPoolEntries в items.ts
   */
  private mapItemId(itemId: string): string {
    const itemMapping: Record<string, string> = {
      itm_credit_chip: 'credits',
      itm_flux_shard: 'ore_crystal', // временный маппинг
      itm_quartz_ore: 'ore_stone', // временный маппинг
      itm_ferrospark_ore: 'ore_crystal', // временный маппинг
      itm_scrap_metal: 'ore_stone', // временный маппинг
      itm_lumenite_crystal: 'ore_crystal', // временный маппинг
      itm_neon_dust: 'ore_stone', // временный маппинг
      itm_neon_vein_shard: 'ore_crystal', // временный маппинг
    };

    return itemMapping[itemId] || itemId;
  }

  /**
   * Получить таблицу дропа по ID
   */
  getLootTable(tableId: string): LootTable | null {
    return this.lootTables.get(tableId) || null;
  }

  /**
   * Генерировать дроп из таблицы
   */
  generateLoot(tableId: string): Array<{ itemId: string; quantity: number }> {
    const table = this.getLootTable(tableId);
    if (!table) {
      logger.warn('Таблица дропа не найдена', { tableId, source: 'loot' });
      return [];
    }

    const loot: Array<{ itemId: string; quantity: number }> = [];
    const rolls = table.rolls || 1;

    // Выполняем указанное количество роллов
    for (let roll = 0; roll < rolls; roll++) {
      const selectedEntry = this.selectWeightedEntry(table.entries, table.totalWeight);
      if (!selectedEntry) continue;

      const quantity = this.randomBetween(selectedEntry.minQuantity, selectedEntry.maxQuantity);

      // Проверяем, что предмет существует в конфиге
      if (!itemsDB.hasItem(selectedEntry.itemId)) {
        logger.warn('Предмет не найден в конфиге', {
          itemId: selectedEntry.itemId,
          source: 'loot',
        });
        continue;
      }

      // Объединяем одинаковые предметы
      const existingLoot = loot.find(item => item.itemId === selectedEntry.itemId);
      if (existingLoot) {
        existingLoot.quantity += quantity;
      } else {
        loot.push({
          itemId: selectedEntry.itemId,
          quantity,
        });
      }
    }

    logger.info('Дроп сгенерирован', {
      tableId,
      rolls,
      lootCount: loot.length,
      source: 'loot',
    });

    return loot;
  }

  /**
   * Генерировать дроп с взвешенным выбором
   */
  generateWeightedLoot(
    tableId: string,
    count: number = 1
  ): Array<{ itemId: string; quantity: number }> {
    const table = this.getLootTable(tableId);
    if (!table) {
      logger.warn('Таблица дропа не найдена', { tableId, source: 'loot' });
      return [];
    }

    const loot: Array<{ itemId: string; quantity: number }> = [];

    for (let i = 0; i < count; i++) {
      const selectedEntry = this.selectWeightedEntry(table.entries, table.totalWeight);
      if (!selectedEntry) continue;

      const quantity = this.randomBetween(selectedEntry.minQuantity, selectedEntry.maxQuantity);

      // Проверяем, что предмет существует
      if (!itemsDB.hasItem(selectedEntry.itemId)) {
        logger.warn('Предмет не найден в конфиге', {
          itemId: selectedEntry.itemId,
          source: 'loot',
        });
        continue;
      }

      loot.push({
        itemId: selectedEntry.itemId,
        quantity,
      });
    }

    return loot;
  }

  /**
   * Выбрать запись по весу
   */
  private selectWeightedEntry(entries: LootEntry[], totalWeight: number): LootEntry | null {
    const random = Math.random() * totalWeight;
    let currentWeight = 0;

    for (const entry of entries) {
      currentWeight += entry.weight;
      if (random <= currentWeight) {
        return entry;
      }
    }

    return null;
  }

  /**
   * Случайное число между min и max (включительно)
   */
  private randomBetween(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Получить все таблицы дропа
   */
  getAllLootTables(): LootTable[] {
    return Array.from(this.lootTables.values());
  }

  /**
   * Получить статистику таблицы дропа
   */
  getLootTableStats(tableId: string) {
    const table = this.getLootTable(tableId);
    if (!table) return null;

    return {
      tableId: table.tableId,
      name: table.name,
      entriesCount: table.entries.length,
      totalWeight: table.totalWeight,
      entries: table.entries.map(entry => ({
        itemId: entry.itemId,
        quantityRange: `${entry.minQuantity}-${entry.maxQuantity}`,
        weight: entry.weight,
      })),
    };
  }
}

export const lootDB = LootDB.getInstance();
